import { Link, useNavigate } from "react-router-dom";
import { Home, ArrowLeft, RefreshCw } from "lucide-react";
import { BrandLogo } from "../../components/BrandLogo";
import { ThemeToggle } from "../../components/ThemeToggle";
import { useTheme } from "../../utils/ThemeProvider";
import { cn } from "../../lib/utils";

/**
 * Shared branded shell for HTTP / app error states.
 */
export function ErrorPage({
  code,
  title,
  description,
  icon: Icon,
  primaryAction,
  secondaryAction,
  children,
}) {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  return (
    <div
      className={cn(
        "login-shell relative min-h-[100svh] overflow-x-hidden overflow-y-auto flex flex-col",
        isDark ? "bg-[var(--login-navy-deep)] text-white" : "bg-background text-foreground"
      )}
    >
      {isDark ? (
        <>
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(11,30,122,0.95), transparent 55%), linear-gradient(160deg, #0b1e7a 0%, #060f42 55%, #040a2e 100%)",
            }}
          />
          <div className="login-facet absolute inset-0 opacity-60" />
          <div className="login-glow-orb absolute -top-24 -left-16 h-80 w-80 rounded-full bg-[var(--login-gold)]/20 blur-3xl" />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(135deg, #bfdbfe 0%, #c7d2fe 50%, #ddd6fe 100%)",
            }}
          />
          <div className="login-facet login-facet-light absolute inset-0 opacity-70" />
          <div className="login-glow-orb absolute -top-24 -left-16 h-80 w-80 rounded-full bg-[var(--login-gold)]/25 blur-3xl" />
        </>
      )}

      <div className="relative z-10 flex items-center justify-between px-4 sm:px-6 py-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <Link to="/" className="inline-flex items-center gap-2 min-h-11">
          <BrandLogo imgClassName="h-9 w-9" />
          <span
            className={cn(
              "hidden sm:inline text-sm font-semibold tracking-tight",
              isDark ? "text-white" : "text-foreground"
            )}
          >
            CELSZC
          </span>
        </Link>
        <ThemeToggle variant={isDark ? "brand" : "panel"} className="min-h-11 min-w-11 h-11 w-11" />
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <div
          className={cn(
            "w-full max-w-lg rounded-3xl p-6 sm:p-10 text-center",
            isDark
              ? "border border-white/15 bg-white/[0.07] backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.35)]"
              : "border border-border bg-card/90 shadow-lg backdrop-blur-sm"
          )}
        >
          {Icon && (
            <div
              className={cn(
                "mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl",
                isDark ? "bg-[#c9a227]/15 text-[#e0c15a]" : "bg-primary/10 text-primary"
              )}
            >
              <Icon className="h-7 w-7" aria-hidden />
            </div>
          )}

          <p
            className={cn(
              "text-[11px] uppercase tracking-[0.35em] font-semibold",
              isDark ? "text-[#e0c15a]/90" : "text-primary"
            )}
          >
            Error {code}
          </p>
          <h1
            className={cn(
              "mt-3 text-3xl sm:text-4xl font-semibold tracking-tight",
              isDark ? "text-white" : "text-foreground"
            )}
          >
            {title}
          </h1>
          <p
            className={cn(
              "mt-3 text-sm sm:text-base leading-relaxed",
              isDark ? "text-white/65" : "text-muted-foreground"
            )}
          >
            {description}
          </p>

          {children}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {primaryAction || (
              <Link
                to="/"
                className={cn(
                  "inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold transition",
                  isDark
                    ? "bg-[#c9a227] text-[#1a1405] hover:bg-[#e0c15a]"
                    : "bg-primary text-primary-foreground hover:brightness-110"
                )}
              >
                <Home className="h-4 w-4" />
                Go home
              </Link>
            )}
            {secondaryAction || (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={cn(
                  "inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold transition border",
                  isDark
                    ? "border-white/25 text-white hover:bg-white/10"
                    : "border-border text-foreground hover:bg-muted"
                )}
              >
                <ArrowLeft className="h-4 w-4" />
                Go back
              </button>
            )}
          </div>
        </div>

        <p
          className={cn(
            "mt-8 text-xs",
            isDark ? "text-white/40" : "text-muted-foreground"
          )}
        >
          Christ Embassy Lagos Sub Zone C
        </p>
      </div>
    </div>
  );
}

export function ErrorActionButton({ children, onClick, variant = "primary", className }) {
  const { isDark } = useTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold transition",
        variant === "primary"
          ? isDark
            ? "bg-[#c9a227] text-[#1a1405] hover:bg-[#e0c15a]"
            : "bg-primary text-primary-foreground hover:brightness-110"
          : isDark
            ? "border border-white/25 text-white hover:bg-white/10"
            : "border border-border text-foreground hover:bg-muted",
        className
      )}
    >
      {children}
    </button>
  );
}

export { RefreshCw, Home, ArrowLeft };
